import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Pencil, Search, X, BarChart3 } from "lucide-react";

const emptyForm = {
    name: "", gstin: "", contact: "", email: "", address: "",
    state: "", state_code: "", category: "", notes: "", active: true,
};

export default function VendorsPage() {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/vendors", { params: { active_only: false, q } }).then((r) => setRows(r.data));
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

    const openAdd = () => { setEditingId(null); setForm(emptyForm); setShowModal(true); };
    const openEdit = (v) => { setEditingId(v.id); setForm({ ...emptyForm, ...v }); setShowModal(true); };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return toast.error("Vendor name is required");
        setBusy(true);
        try {
            if (editingId) {
                await api.put(`/vendors/${editingId}`, form);
                toast.success(`${form.name} updated`);
            } else {
                await api.post("/vendors", form);
                toast.success(`${form.name} added`);
            }
            setShowModal(false);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        setBusy(false);
    };

    const remove = async (v) => {
        if (!window.confirm(`Delete vendor ${v.name}?`)) return;
        try {
            await api.delete(`/vendors/${v.id}`);
            toast.success(`${v.name} removed`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    return (
        <div data-testid="vendors-page">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl hdb-heading font-black text-slate-900">Vendor Master</h1>
                    <p className="text-slate-500 mt-1 text-sm">
                        Suppliers and marketplaces you buy from. Auto-populated later when you upload
                        vendor bills — manageable manually here.
                    </p>
                </div>
                <button className="hdb-btn hdb-btn-primary" onClick={openAdd} data-testid="add-vendor-btn">
                    <Plus size={14} /> Add Vendor
                </button>
            </div>

            <div className="hdb-card p-4 mb-5">
                <div className="relative">
                    <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
                    <input className="hdb-input pl-9" placeholder="Search by name, GSTIN or contact…"
                           value={q} onChange={(e) => setQ(e.target.value)} data-testid="vendor-search" />
                </div>
            </div>

            <div className="hdb-card overflow-hidden">
                <table className="hdb-table w-full">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>GSTIN</th>
                            <th>Contact</th>
                            <th>State</th>
                            <th>Category</th>
                            <th className="w-32"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((v) => (
                            <tr key={v.id} data-testid={`vendor-row-${v.name}`}
                                className={!v.active ? "opacity-50" : ""}>
                                <td>
                                    <div className="font-medium text-slate-900">{v.name}</div>
                                    {v.address && <div className="text-xs text-slate-500 mt-0.5">{v.address}</div>}
                                </td>
                                <td className="hdb-mono text-xs">{v.gstin || "—"}</td>
                                <td className="hdb-mono text-xs">{v.contact || v.email || "—"}</td>
                                <td className="text-xs">{v.state ? `${v.state}${v.state_code ? ` · ${v.state_code}` : ""}` : "—"}</td>
                                <td>{v.category ? <span className="hdb-tag hdb-tag-slate">{v.category}</span> : "—"}</td>
                                <td>
                                    <div className="flex gap-1 justify-end">
                                        <Link to={`/vendors/${v.id}`} className="hdb-btn hdb-btn-mint"
                                              title="Open vendor dashboard — purchases, ITC by slab, trends"
                                              data-testid={`view-dashboard-${v.name}`}>
                                            <BarChart3 size={12} />
                                        </Link>
                                        <button className="hdb-btn hdb-btn-ghost" onClick={() => openEdit(v)}
                                                data-testid={`edit-vendor-${v.name}`}>
                                            <Pencil size={12} />
                                        </button>
                                        <button className="hdb-btn hdb-btn-danger" onClick={() => remove(v)}
                                                data-testid={`delete-vendor-${v.name}`}>
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={6} className="text-center py-12 text-slate-400">
                                <Building2 size={22} className="mx-auto mb-2 opacity-60" />
                                No vendors yet. Add your first supplier.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
                     data-testid="vendor-modal">
                    <div className="hdb-card w-full max-w-2xl p-6 shadow-2xl">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="hdb-heading font-bold text-lg text-slate-900">
                                {editingId ? "Edit vendor" : "Add vendor"}
                            </h3>
                            <button className="text-slate-400 hover:text-slate-600" onClick={() => setShowModal(false)}>
                                <X size={18} />
                            </button>
                        </div>
                        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                                <label className="hdb-label">Vendor name *</label>
                                <input className="hdb-input" value={form.name}
                                       onChange={(e) => setForm({ ...form, name: e.target.value })}
                                       required data-testid="vendor-name" />
                            </div>
                            <div>
                                <label className="hdb-label">GSTIN</label>
                                <input className="hdb-input hdb-mono" value={form.gstin}
                                       onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                                       data-testid="vendor-gstin" />
                            </div>
                            <div>
                                <label className="hdb-label">Category</label>
                                <input className="hdb-input" value={form.category}
                                       onChange={(e) => setForm({ ...form, category: e.target.value })}
                                       placeholder="e.g. Grains, Utilities, Marketing" />
                            </div>
                            <div>
                                <label className="hdb-label">Contact</label>
                                <input className="hdb-input hdb-mono" value={form.contact}
                                       onChange={(e) => setForm({ ...form, contact: e.target.value })}
                                       data-testid="vendor-contact" />
                            </div>
                            <div>
                                <label className="hdb-label">Email</label>
                                <input className="hdb-input" type="email" value={form.email}
                                       onChange={(e) => setForm({ ...form, email: e.target.value })} />
                            </div>
                            <div className="col-span-2">
                                <label className="hdb-label">Address</label>
                                <input className="hdb-input" value={form.address}
                                       onChange={(e) => setForm({ ...form, address: e.target.value })} />
                            </div>
                            <div>
                                <label className="hdb-label">State</label>
                                <input className="hdb-input" value={form.state}
                                       onChange={(e) => setForm({ ...form, state: e.target.value })} />
                            </div>
                            <div>
                                <label className="hdb-label">State code</label>
                                <input className="hdb-input hdb-mono" value={form.state_code}
                                       onChange={(e) => setForm({ ...form, state_code: e.target.value })} />
                            </div>
                            <div className="col-span-2">
                                <label className="hdb-label">Notes</label>
                                <input className="hdb-input" value={form.notes}
                                       onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                            </div>
                            <label className="col-span-2 flex items-center gap-2 text-sm text-slate-600">
                                <input type="checkbox" checked={form.active}
                                       onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                                Active — appears in dropdowns and reports
                            </label>
                            <div className="col-span-2 flex justify-end gap-2 mt-2">
                                <button type="button" className="hdb-btn hdb-btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="hdb-btn hdb-btn-primary" disabled={busy} data-testid="save-vendor">
                                    {busy ? "Saving…" : editingId ? "Update" : "Add vendor"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
